import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, authFetch } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Car, Filter, Send, CheckCircle2, ExternalLink, Loader2, MailCheck,
  FileDown, Share2, Edit3, Skull,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { useAuth } from "../context/AuthContext";
import HousekeepingEditModal from "../components/HousekeepingEditModal";

const CAR_STATUS_PILL = {
  to_order: { label: "To order", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" },
  waiting_confirmation: { label: "Waiting confirmation", cls: "bg-[#FBF7EF] text-[#6B705C] border-[#E8E1D5]" },
  booked: { label: "Booked", cls: "bg-[#E6F0E8] text-[#3F6B4F] border-[#C9DAC9]" },
  cancelled: { label: "Cancelled", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6] opacity-60" },
  covered: { label: "Covered", cls: "bg-[#FBF7EF] text-[#9A9A9A] border-[#E8E1D5]" },
};
const HK_STATUS_PILL = {
  to_send: { label: "To send", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" },
  sent: { label: "Sent", cls: "bg-[#FBF7EF] text-[#6B705C] border-[#E8E1D5]" },
  confirmed: { label: "Confirmed", cls: "bg-[#E6F0E8] text-[#3F6B4F] border-[#C9DAC9]" },
  cancelled: { label: "Cancelled", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6] opacity-60" },
};

export default function Operations() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [carEmail, setCarEmail] = useState(null);
  const [hkEdit, setHkEdit] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("upcoming");
  const [hkPage, setHkPage] = useState(0);
  const [carPage, setCarPage] = useState(0);
  const [hardDeleteOp, setHardDeleteOp] = useState(null); // {request_id, expected, label, type}
  const [hardDeleteInput, setHardDeleteInput] = useState("");
  const PAGE_SIZE = 20;

  const load = () => api.get("/requests").then((r) => setRequests(r.data));
  useEffect(() => { load().catch(console.error); }, []);

  const openHardDelete = (r) => {
    if (r.type === "housekeeping") {
      setHardDeleteOp({ request_id: r.request_id, expected: r.ref_number || "", label: "reference number", type: "housekeeping" });
    } else {
      const title = (r.booking?.title || "Family stay").trim();
      setHardDeleteOp({ request_id: r.request_id, expected: title, label: "booking title", type: "car" });
    }
    setHardDeleteInput("");
  };
  const confirmHardDelete = async () => {
    if (!hardDeleteOp) return;
    try {
      await api.delete(`/requests/${hardDeleteOp.request_id}/hard`, { data: { confirm: hardDeleteInput } });
      toast.success("Operation permanently deleted");
      setHardDeleteOp(null);
      setHardDeleteInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const isToAction = (r) => {
    if (r.type === "car") return ["to_order", "waiting_confirmation"].includes(r.status);
    return ["to_send", "sent"].includes(r.status); // housekeeping
  };
  const matchesStatus = (r, b) => {
    if (filterStatus === "all") return true;
    if (filterStatus === "upcoming") {
      // Show upcoming stays regardless of operation status (use departure date so an in-progress stay still shows)
      return (b?.departure_date || r.scheduled_date || "") >= today;
    }
    // to_action
    return isToAction(r) && (b?.departure_date || r.scheduled_date || "") >= today;
  };

  const cars = useMemo(() => {
    return requests
      .filter((r) => r.type === "car" && r.status !== "covered")
      .filter((r) => matchesStatus(r, r.booking))
      .sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, filterStatus]);

  const hk = useMemo(() => {
    return requests
      .filter((r) => r.type === "housekeeping" && r.status !== "covered")
      .filter((r) => matchesStatus(r, r.booking))
      .sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests, filterStatus]);

  // Reset pagination when filters change
  useEffect(() => { setHkPage(0); setCarPage(0); }, [filterType, filterStatus]);

  const hkPaged = hk.slice(hkPage * PAGE_SIZE, (hkPage + 1) * PAGE_SIZE);
  const carPaged = cars.slice(carPage * PAGE_SIZE, (carPage + 1) * PAGE_SIZE);
  const hkTotalPages = Math.max(1, Math.ceil(hk.length / PAGE_SIZE));
  const carTotalPages = Math.max(1, Math.ceil(cars.length / PAGE_SIZE));

  const setStatus = async (rid, status) => {
    try { await api.put(`/requests/${rid}`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Could not update"); }
  };

  // ----- Car actions
  const previewCarEmail = async (bookingId) => {
    setBusy(`preview-${bookingId}`);
    try {
      const { data } = await api.get(`/bookings/${bookingId}/car-email`);
      setCarEmail({ ownerBookingId: bookingId, payload: data });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load car email"); }
    finally { setBusy(null); }
  };
  const sendCarEmail = async (bookingId) => {
    setBusy(`send-${bookingId}`);
    try { await api.post(`/bookings/${bookingId}/car-email/send`); toast.success("Car email sent"); setCarEmail(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(null); }
  };
  const markCarSent = async (bookingId) => {
    setBusy(`marksent-${bookingId}`);
    try { await api.post(`/operations/car/${bookingId}/mark-sent`); toast.success("Marked as sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not mark sent"); }
    finally { setBusy(null); }
  };
  const markCarConfirmed = async (bookingId) => {
    setBusy(`confirm-${bookingId}`);
    try { await api.post(`/operations/car/${bookingId}/mark-confirmed`); toast.success("Confirmed — emails sent to guests"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); }
    finally { setBusy(null); }
  };

  // ----- Housekeeping actions
  const fetchHkPdf = async (booking_id, asAttachment = false) => {
    const url = `${API}/operations/housekeeping/${booking_id}/pdf${asAttachment ? "?download=1" : ""}`;
    const res = await authFetch(url);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || "PDF fetch failed");
    }
    return res.blob();
  };
  const downloadHkPdf = async (booking_id, ref) => {
    setBusy(`pdf-${booking_id}`);
    try {
      const blob = await fetchHkPdf(booking_id, true);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Casa Blunt ${ref || booking_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(e.message || "Could not generate PDF"); }
    finally { setBusy(null); }
  };
  const sendToCleaner = async (booking_id, ref, dates) => {
    setBusy(`hk-share-${booking_id}`);
    try {
      const blob = await fetchHkPdf(booking_id);
      const file = new File([blob], `Casa Blunt ${ref || booking_id}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Casa Blunt — Cleaner brief",
          text: `Cleaner brief — ${dates}`,
        });
        await api.post(`/operations/housekeeping/${booking_id}/mark-sent`).catch(() => {});
        toast.success("Sent — marked as Sent");
        load();
      } else {
        // Desktop fallback: download PDF + open WhatsApp web with prefilled text
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Casa Blunt ${ref || booking_id}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        const { data } = await api.get(`/operations/housekeeping/${booking_id}/whatsapp`);
        window.open(data.wa_url, "_blank", "noopener");
        await api.post(`/operations/housekeeping/${booking_id}/mark-sent`).catch(() => {});
        toast.success("PDF downloaded & WhatsApp opened — drop the PDF in. Marked as Sent.");
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share PDF");
    } finally { setBusy(null); }
  };
  const markHkSent = async (booking_id) => {
    setBusy(`hk-sent-${booking_id}`);
    try { await api.post(`/operations/housekeeping/${booking_id}/mark-sent`); toast.success("Marked as sent"); load(); }
    catch { toast.error("Could not mark sent"); }
    finally { setBusy(null); }
  };
  const markHkConfirmed = async (booking_id) => {
    setBusy(`hk-confirm-${booking_id}`);
    try { await api.post(`/operations/housekeeping/${booking_id}/mark-confirmed`); toast.success("Confirmed"); load(); }
    catch { toast.error("Could not confirm"); }
    finally { setBusy(null); }
  };

  const mailtoFor = (payload) => {
    const params = new URLSearchParams();
    params.set("subject", payload.subject || "");
    params.set("body", payload.body || "");
    return `mailto:${encodeURIComponent(payload.to || "")}?${params.toString()}`;
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto" data-testid="operations-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Operaciones</p>
        <h1 className="font-serif-villa text-4xl">Operations</h1>
        <p className="text-sm text-[#7A7A7A] mt-1">Cars, housekeeping & more — keep the casa running smoothly.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 villa-card p-4">
        <Filter className="w-4 h-4 text-[#7A7A7A]" />
        <Pill active={filterType === "all"} onClick={() => setFilterType("all")} testid="rtype-all">All</Pill>
        <Pill active={filterType === "housekeeping"} onClick={() => setFilterType("housekeeping")} testid="rtype-housekeeping">Housekeeping</Pill>
        <Pill active={filterType === "car"} onClick={() => setFilterType("car")} testid="rtype-car">Car</Pill>
        <span className="mx-2 text-[#E8E1D5]">|</span>
        <Pill active={filterStatus === "to_action"} onClick={() => setFilterStatus("to_action")} testid="rstatus-to-action">To action</Pill>
        <Pill active={filterStatus === "upcoming"} onClick={() => setFilterStatus("upcoming")} testid="rstatus-upcoming">Upcoming</Pill>
        <Pill active={filterStatus === "all"} onClick={() => setFilterStatus("all")} testid="rstatus-all">All</Pill>
      </div>

      {/* Housekeeping */}
      {(filterType === "all" || filterType === "housekeeping") && hk.length > 0 && (
        <section className="space-y-3" data-testid="ops-hk-section">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif-villa text-2xl">Casa · housekeeping</h2>
            <span className="text-xs text-[#9A9A9A]">{hk.length} total</span>
          </div>
          <ul className="space-y-3">
            {hkPaged.map((r) => {
              const pill = HK_STATUS_PILL[r.status] || HK_STATUS_PILL.to_send;
              const b = r.booking || {};
              return (
                <li key={r.request_id} className="villa-card p-5" data-testid={`hk-row-${r.booking_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#6B705C]" />
                        <span className="font-medium text-[#2B2B2B]">{b.title || "Family stay"}</span>
                        {r.ref_number && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FBF7EF] border border-[#E8E1D5] text-[#6B705C]" data-testid={`hk-ref-${r.booking_id}`}>{r.ref_number}</span>
                        )}
                        <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${pill.cls}`} data-testid={`hk-status-pill-${r.booking_id}`}>{pill.label}</span>
                      </div>
                      <div className="text-xs text-[#7A7A7A] mt-1">
                        {b.arrival_date} → {b.departure_date}
                        {(r.adults_count || r.children_count) ? <> · {r.adults_count || 0} adult{r.adults_count === 1 ? "" : "s"}{r.children_count ? `, ${r.children_count} child${r.children_count === 1 ? "" : "ren"}` : ""}</> : null}
                      </div>
                      <Link to={`/bookings/${r.booking_id}`} className="text-xs text-[#C25E3E] hover:underline mt-1 inline-block">View stay →</Link>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => setHkEdit(r)}
                        data-testid={`hk-edit-${r.booking_id}`}
                        className="px-3 py-2 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1.5"
                      ><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                      <button
                        onClick={() => downloadHkPdf(r.booking_id, r.ref_number)}
                        data-testid={`hk-pdf-${r.booking_id}`}
                        disabled={busy === `pdf-${r.booking_id}`}
                        className="px-3 py-2 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1.5"
                      >{busy === `pdf-${r.booking_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF</button>
                      {user?.is_admin && (
                        <button
                          onClick={() => sendToCleaner(r.booking_id, r.ref_number, `${b.arrival_date} → ${b.departure_date}`)}
                          data-testid={`hk-share-${r.booking_id}`}
                          disabled={busy === `hk-share-${r.booking_id}`}
                          className="px-3 py-2 rounded-full text-xs bg-[#25D366]/10 border border-[#25D366]/40 text-[#1F8A4C] hover:bg-[#25D366]/20 inline-flex items-center gap-1.5"
                        >{busy === `hk-share-${r.booking_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />} Send to cleaner</button>
                      )}
                      {user?.is_admin && r.status === "to_send" && (
                        <button
                          onClick={() => markHkSent(r.booking_id)}
                          data-testid={`hk-mark-sent-${r.booking_id}`}
                          className="px-3 py-2 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1.5"
                        ><Send className="w-3.5 h-3.5" /> Mark sent</button>
                      )}
                      {user?.is_admin && r.status === "sent" && (
                        <button
                          onClick={() => markHkConfirmed(r.booking_id)}
                          data-testid={`hk-mark-confirmed-${r.booking_id}`}
                          className="px-3 py-2 rounded-full text-xs bg-[#6B705C] text-[#FDFBF7] hover:bg-[#5A6050] inline-flex items-center gap-1.5"
                        ><CheckCircle2 className="w-3.5 h-3.5" /> Mark confirmed</button>
                      )}
                      {user?.is_admin && (
                        <button
                          onClick={() => openHardDelete(r)}
                          data-testid={`hk-hard-delete-${r.booking_id}`}
                          title="Delete operation permanently (admin only)"
                          className="px-2 py-2 rounded-full text-xs bg-[#2B2B2B] text-[#FBE9E7] hover:bg-[#1A1A1A] inline-flex items-center gap-1.5"
                        ><Skull className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination page={hkPage} totalPages={hkTotalPages} setPage={setHkPage} testidPrefix="hk" />
        </section>
      )}

      {/* Cars */}
      {(filterType === "all" || filterType === "car") && cars.length > 0 && (
        <section className="space-y-3" data-testid="ops-cars-section">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif-villa text-2xl">Coche · car bookings</h2>
            <span className="text-xs text-[#9A9A9A]">{cars.length} total</span>
          </div>
          <ul className="space-y-3">
            {carPaged.map((r) => {
              const pill = CAR_STATUS_PILL[r.status] || CAR_STATUS_PILL.to_order;
              const b = r.booking || {};
              return (
                <li key={r.request_id} className="villa-card p-5" data-testid={`car-row-${r.booking_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-[#457B9D]" />
                        <span className="font-medium text-[#2B2B2B]">{b.title || "Family stay"}</span>
                        <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${pill.cls}`} data-testid={`car-status-pill-${r.booking_id}`}>{pill.label}</span>
                      </div>
                      <div className="text-xs text-[#7A7A7A] mt-1">{b.arrival_date} → {b.departure_date}</div>
                      <Link to={`/bookings/${r.booking_id}`} className="text-xs text-[#C25E3E] hover:underline mt-1 inline-block">View stay →</Link>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {user?.is_admin && r.status === "to_order" && (
                        <button
                          data-testid={`car-preview-${r.booking_id}`}
                          disabled={busy?.startsWith("preview-")}
                          onClick={() => previewCarEmail(r.booking_id)}
                          className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2"
                        ><Send className="w-4 h-4" /> Order car</button>
                      )}
                      {r.status === "waiting_confirmation" && (
                        <>
                          <span className="text-xs text-[#7A7A7A]">Sent {r.sent_at ? format(parseISO(r.sent_at), "d MMM, HH:mm") : ""}</span>
                          {user?.is_admin && (
                            <button
                              data-testid={`car-confirm-${r.booking_id}`}
                              disabled={busy?.startsWith("confirm-")}
                              onClick={() => markCarConfirmed(r.booking_id)}
                              className="px-4 py-2 rounded-full text-sm bg-[#6B705C] text-[#FDFBF7] hover:bg-[#5A6050] inline-flex items-center gap-2"
                            >{busy === `confirm-${r.booking_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark confirmed</button>
                          )}
                        </>
                      )}
                      {r.status === "booked" && (
                        <span className="text-xs text-[#3F6B4F] inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Confirmed{r.confirmed_at ? ` · ${format(parseISO(r.confirmed_at), "d MMM")}` : ""}</span>
                      )}
                      {user?.is_admin && (
                        <button
                          onClick={() => openHardDelete(r)}
                          data-testid={`car-hard-delete-${r.booking_id}`}
                          title="Delete operation permanently (admin only)"
                          className="px-2 py-2 rounded-full text-xs bg-[#2B2B2B] text-[#FBE9E7] hover:bg-[#1A1A1A] inline-flex items-center gap-1.5"
                        ><Skull className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <Pagination page={carPage} totalPages={carTotalPages} setPage={setCarPage} testidPrefix="car" />
        </section>
      )}

      {requests.length === 0 && (
        <div className="villa-card p-10 text-center text-sm text-[#9A9A9A] italic">Nothing matches — la casa está tranquila.</div>
      )}

      {carEmail && (
        <CarEmailModal
          payload={carEmail.payload}
          ownerBookingId={carEmail.ownerBookingId}
          busy={busy}
          mailto={mailtoFor(carEmail.payload)}
          onClose={() => setCarEmail(null)}
          onSend={() => sendCarEmail(carEmail.ownerBookingId)}
          onMailedOutside={() => { markCarSent(carEmail.ownerBookingId); setCarEmail(null); }}
        />
      )}

      {hkEdit && (
        <HousekeepingEditModal
          request={hkEdit}
          onClose={() => setHkEdit(null)}
          onSaved={() => { setHkEdit(null); load(); }}
        />
      )}

      {hardDeleteOp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2B2B2B]/60 p-4" data-testid="op-hard-delete-modal">
          <div className="bg-[#FDFBF7] rounded-2xl p-6 sm:p-8 max-w-md w-full border-2 border-[#A84C30] shadow-[0_30px_60px_rgba(168,76,48,0.4)]">
            <h3 className="font-serif-villa text-2xl text-[#A84C30] inline-flex items-center gap-2"><Skull className="w-5 h-5" /> Delete this operation?</h3>
            <p className="text-sm text-[#7A7A7A] mt-2">
              Permanently removes this {hardDeleteOp.type === "housekeeping" ? "cleaner brief" : "car booking"}. The stay itself is kept. <strong>Cannot be undone.</strong>
            </p>
            <p className="text-sm text-[#7A7A7A] mt-3">Type the {hardDeleteOp.label} exactly to confirm:</p>
            <p className="mt-2 text-sm text-[#2B2B2B] font-mono bg-[#F5F0E6] rounded-lg px-3 py-2 border border-[#E8E1D5]">
              {hardDeleteOp.expected || "(no value — cannot delete)"}
            </p>
            <input
              autoFocus
              data-testid="op-hard-delete-confirm-input"
              className="mt-4 w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#A84C30]/40 focus:border-[#A84C30]"
              placeholder={`Type the ${hardDeleteOp.label} here`}
              value={hardDeleteInput}
              onChange={(e) => setHardDeleteInput(e.target.value)}
            />
            <div className="mt-5 flex items-center gap-2 justify-end">
              <button onClick={() => { setHardDeleteOp(null); setHardDeleteInput(""); }} className="px-5 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6]">Keep</button>
              <button
                onClick={confirmHardDelete}
                disabled={!hardDeleteOp.expected || hardDeleteInput.trim() !== hardDeleteOp.expected}
                data-testid="op-hard-delete-confirm-btn"
                className="px-5 py-2 rounded-full text-sm bg-[#2B2B2B] text-[#FBE9E7] hover:bg-[#1A1A1A] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 border border-[#2B2B2B]"
              >
                <Skull className="w-4 h-4" /> Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CarEmailModal({ payload, busy, mailto, onClose, onSend, onMailedOutside }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#2B2B2B]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="car-email-modal">
      <div className="bg-[#FDFBF7] rounded-2xl border border-[#E8E1D5] max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 border-b border-[#E8E1D5]">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Coche</p>
          <h3 className="font-serif-villa text-2xl">Order the hire car</h3>
          {payload.cluster_size > 1 && (
            <p className="text-xs text-[#6B705C] mt-1">Spans {payload.cluster_size} overlapping stays — handover handled automatically.</p>
          )}
        </div>
        <div className="p-5 sm:p-6 space-y-3 overflow-y-auto">
          <div className="text-xs text-[#7A7A7A]">To: {payload.to || "—"}</div>
          <div className="text-sm font-medium">{payload.subject}</div>
          <pre className="text-xs whitespace-pre-wrap bg-[#FBF7EF] border border-[#E8E1D5] rounded-xl p-4 text-[#2B2B2B] font-mono leading-relaxed">{payload.body}</pre>
        </div>
        <div className="p-5 sm:p-6 border-t border-[#E8E1D5] flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-[#7A7A7A] hover:text-[#2B2B2B]" data-testid="car-modal-close">Cancel</button>
          <a href={mailto} onClick={onMailedOutside} data-testid="car-modal-mailto"
            className="px-4 py-2 rounded-full text-sm bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> Open in mail app
          </a>
          <button onClick={onSend} disabled={busy?.startsWith("send-") || !payload.to} data-testid="car-modal-send"
            className="px-4 py-2 rounded-full text-sm bg-[#C25E3E] text-[#FDFBF7] hover:bg-[#A84C30] inline-flex items-center gap-2 disabled:opacity-50">
            {busy?.startsWith("send-") ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailCheck className="w-4 h-4" />} Send via email
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, setPage, testidPrefix }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-2" data-testid={`${testidPrefix}-pagination`}>
      <button
        type="button"
        onClick={() => setPage(Math.max(0, page - 1))}
        disabled={page === 0}
        data-testid={`${testidPrefix}-page-prev`}
        className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F5F0E6]"
      >← Prev</button>
      <span className="text-xs text-[#7A7A7A]" data-testid={`${testidPrefix}-page-indicator`}>Page {page + 1} of {totalPages}</span>
      <button
        type="button"
        onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
        data-testid={`${testidPrefix}-page-next`}
        className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F5F0E6]"
      >Next →</button>
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${
        active ? "bg-[#2B2B2B] text-[#FDFBF7]" : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
      }`}>{children}</button>
  );
}
